import { useState } from "react";
import {
  Badge,
  DataTable,
  Field,
  LoadState,
  Modal,
  Page,
  Panel,
  Stats,
  money,
  useFinanceData,
} from "layouts/finance/shared";
import {
  financeApi,
  subUserParams,
  subUserRows,
  totalCount,
} from "services/FinanceService";
import useAdminMutation from "./useAdminMutation";

const complianceStates = ["ACTIVE", "SUSPENDED", "TERMINATED"];

export default function UserManagement() {
  const summary = useFinanceData("/sub-users/summary");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [merchantQuery, setMerchantQuery] = useState("");
  const [merchantId, setMerchantId] = useState("");
  const [target, setTarget] = useState(null);
  const [overrideStatus, setOverrideStatus] = useState("SUSPENDED");
  const [reason, setReason] = useState("");

  const directory = useFinanceData(
    "/sub-users",
    subUserParams(page, size, { search, status, merchantId })
  );
  const mutation = useAdminMutation(() => {
    directory.refresh();
    summary.refresh();
  });
  const rows = subUserRows(directory.data);
  const totalPages = directory.data?.totalPages;
  const total = totalCount(directory.data);
  const hasNext =
    totalPages != null ? page + 1 < totalPages : rows.length >= size;

  const openOverride = (row) => {
    mutation.reset();
    setReason("");
    setOverrideStatus(row.status === "ACTIVE" ? "SUSPENDED" : "ACTIVE");
    setTarget(row);
  };

  return (
    <Page
      eyebrow="Administration"
      title="User management"
      description="Search every sub-user across all merchant tenants and apply compliance overrides."
      actions={
        <button
          className="fin-button secondary"
          onClick={() => {
            directory.refresh();
            summary.refresh();
          }}
        >
          Refresh
        </button>
      }
    >
      <LoadState {...summary} />
      {summary.data && (
        <Stats
          items={[
            ["Total sub-users", summary.data.totalSubUsers ?? "—"],
            ["Active", summary.data.activeSubUsers ?? "—"],
            ["Suspended", summary.data.suspendedSubUsers ?? "—"],
            ["Terminated", summary.data.terminatedSubUsers ?? "—"],
            ["Total wallet float", money(summary.data.totalFloatBalance)],
          ]}
        />
      )}

      <Panel>
        <form
          className="toolbar"
          onSubmit={(e) => {
            e.preventDefault();
            setSearch(query.trim());
            setMerchantId(merchantQuery.trim());
            setPage(0);
          }}
        >
          <Field
            label="Search name, BVN, NIN, phone or account"
            name="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search all sub-users"
          />
          <Field
            label="Compliance status"
            name="status"
            value={status}
            options={["", ...complianceStates]}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(0);
            }}
          />
          <Field
            label="Merchant ID"
            name="merchantId"
            value={merchantQuery}
            onChange={(e) => setMerchantQuery(e.target.value)}
            placeholder="All merchants"
          />
          <Field
            label="Page size"
            name="size"
            value={size}
            options={["10", "20", "50", "100"]}
            onChange={(e) => {
              setSize(Number(e.target.value));
              setPage(0);
            }}
          />
          <button className="fin-button" type="submit">
            Search
          </button>
        </form>
      </Panel>

      <LoadState {...directory} />
      {directory.data && (
        <Panel
          title="Sub-user directory"
          subtitle="Cross-merchant results for compliance review."
        >
          <DataTable
            rowKey="id"
            rows={rows}
            columns={[
              { key: "fullName", label: "Full name" },
              { key: "merchantId", label: "Merchant ID" },
              { key: "externalUserRef", label: "External ref" },
              { key: "phoneNumber", label: "Phone" },
              { key: "email", label: "Email" },
              { key: "accountNumber", label: "Account number" },
              {
                key: "balance",
                label: "Balance",
                render: (row) => money(row.balance),
              },
              {
                key: "ledgerBalance",
                label: "Ledger balance",
                render: (row) => money(row.ledgerBalance),
              },
              {
                key: "status",
                label: "Status",
                render: (row) => <Badge value={row.status} />,
              },
              {
                key: "action",
                label: "Compliance",
                render: (row) => (
                  <button
                    className="fin-button secondary"
                    disabled={mutation.busy}
                    onClick={() => openOverride(row)}
                  >
                    {row.status === "ACTIVE" ? "Suspend" : "Reactivate"}
                  </button>
                ),
              },
            ]}
          />
          <div className="pagination">
            <span>
              {total != null
                ? `${total} sub-users`
                : `${rows.length} sub-users on this page`}{" "}
              · Page {page + 1}
              {totalPages != null && ` of ${Math.max(1, totalPages)}`}
            </span>
            <button
              className="fin-button secondary"
              disabled={page === 0 || directory.loading}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Previous
            </button>
            <button
              className="fin-button secondary"
              disabled={directory.loading || !hasNext}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </Panel>
      )}

      {target && (
        <Modal
          title="Compliance status override"
          busy={mutation.busy}
          action="Apply override"
          disabled={!overrideStatus || reason.trim().length < 5}
          onClose={() => setTarget(null)}
          onSubmit={(e) => {
            e.preventDefault();
            if (!overrideStatus || reason.trim().length < 5) return;
            mutation.submit(
              financeApi,
              `/sub-users/${encodeURIComponent(target.id)}/status`,
              {
                method: "PATCH",
                data: { status: overrideStatus, reason: reason.trim() },
              },
              () => setTarget(null),
              `Sub-user status updated to ${overrideStatus}.`
            );
          }}
        >
          <dl className="detail-grid">
            {[
              ["Sub-user", target.fullName],
              ["Merchant ID", target.merchantId],
              ["External ref", target.externalUserRef],
              ["Wallet ID", target.walletId],
              ["Phone", target.phoneNumber],
              ["Email", target.email],
              ["Account number", target.accountNumber],
              ["Bank", target.bankName],
              ["Balance", money(target.balance)],
              ["Ledger balance", money(target.ledgerBalance)],
              ["Current status", target.status],
            ].map(([label, value]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>{value ?? "—"}</dd>
              </div>
            ))}
          </dl>
          <p className="notice warning" role="note">
            A compliance override suspends or terminates the sub-user wallet
            immediately, regardless of the merchant&apos;s own controls.
          </p>
          <Field
            label="New status"
            name="status"
            value={overrideStatus}
            options={complianceStates}
            onChange={(e) => setOverrideStatus(e.target.value)}
          />
          <label className="fin-field">
            <span>Audit reason (at least 5 characters)</span>
            <textarea
              required
              minLength={5}
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          {mutation.error && (
            <p className="notice error" role="alert">
              {mutation.error}
            </p>
          )}
        </Modal>
      )}
    </Page>
  );
}
